import React, { ReactElement } from 'react';
import cn from 'classnames';
import { Checkbox } from '@odin-labs/components';
import {
  PolicyDocumentFragment,
  getPolicyDocumentUrl,
} from 'containers/policyConsent';
import { SmsConsentCheckbox } from 'components/smsConsentCheckbox';
import { kebabOrSnakeToSentenceCase } from 'utils';

const classes = {
  container: cn('odin-mb-2 odin-pt-3 odin-space-y-3'),
  link: cn('odin-text-odin-primary odin-underline'),
  consent: cn('odin-flex odin-items-start odin-text-sm odin-text-gray-700'),
};

export type AccountCreationConsentProps = {
  /** Whether to render the aggregate platform policy-consent checkbox. */
  showPolicyConsent: boolean;
  /** Whether to render the mandatory SMS opt-in checkbox. */
  showSmsConsent: boolean;
  /** Current policy documents, linked from the policy-consent sentence. */
  policyDocuments: PolicyDocumentFragment[];
  hasAgreedToPolicies: boolean;
  onAgreedToPoliciesChange: (checked: boolean) => void;
  hasAgreedToSms: boolean;
  onAgreedToSmsChange: (checked: boolean) => void;
};

/**
 * The consent checkboxes shown beneath the account-creation form: an aggregate
 * platform-policy affirmation (linking each current policy to its page) and the
 * mandatory SMS opt-in. Renders nothing when neither applies. Extracted from
 * {@link AuthAccountCreationStep} to keep that step focused on the form and the
 * create mutation.
 */
export function AccountCreationConsent({
  showPolicyConsent,
  showSmsConsent,
  policyDocuments,
  hasAgreedToPolicies,
  onAgreedToPoliciesChange,
  hasAgreedToSms,
  onAgreedToSmsChange,
}: AccountCreationConsentProps): ReactElement | null {
  if (!showPolicyConsent && !showSmsConsent) {
    return null;
  }

  return (
    <div className={classes.container}>
      {showPolicyConsent && (
        <label className={classes.consent}>
          <Checkbox
            checked={hasAgreedToPolicies}
            onChange={(e): void => onAgreedToPoliciesChange(e.target.checked)}
          />
          {/* Mirrors the re-capture gate's single-sentence consent, but links
              each policy to its PDF rather than bolding it. */}
          <span>
            I have read and agree to the{' '}
            {policyDocuments.map((doc, index) => (
              <React.Fragment key={doc.id}>
                {index > 0 &&
                  (index === policyDocuments.length - 1 ? ' and ' : ', ')}
                <a
                  href={getPolicyDocumentUrl(doc.key)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e): void => e.stopPropagation()}
                  className={classes.link}
                >
                  {kebabOrSnakeToSentenceCase(doc.key)}
                </a>
              </React.Fragment>
            ))}
            .
          </span>
        </label>
      )}
      {showSmsConsent && (
        <SmsConsentCheckbox
          checked={hasAgreedToSms}
          onChange={onAgreedToSmsChange}
        />
      )}
    </div>
  );
}
